// Responsive layout classes (BRIEF owner addition A). CSS media/container
// queries do the visual work; this hook tells components which STRUCTURE to
// mount (bottom bar vs rail, overlay vs docked panel, flyout menu vs column).
//
//   wide     ≥1100   rail 64 + menu 200 + content + docked side panel 420
//   compact  1024–1099  menu folds into the rail (flyout on hover/press)
//   tablet   768–1023   rail icons only; side panel = full-height overlay
//   phone    <768       bottom bar (first five modules + «المزيد»), full-screen panels/dialogs

import { useEffect, useState } from 'react';

export type LayoutClass = 'wide' | 'compact' | 'tablet' | 'phone';

export const BREAKPOINTS = { wide: 1100, compact: 1024, tablet: 768 } as const;

export const LAYOUT_QUERIES: Record<Exclude<LayoutClass, 'phone'>, string> = {
  wide: `(min-width: ${BREAKPOINTS.wide}px)`,
  compact: `(min-width: ${BREAKPOINTS.compact}px)`,
  tablet: `(min-width: ${BREAKPOINTS.tablet}px)`,
};

function matches(q: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
}

export function currentLayout(): LayoutClass {
  if (matches(LAYOUT_QUERIES.wide)) return 'wide';
  if (matches(LAYOUT_QUERIES.compact)) return 'compact';
  if (matches(LAYOUT_QUERIES.tablet)) return 'tablet';
  return 'phone';
}

export function useLayout(): LayoutClass {
  const [layout, setLayout] = useState<LayoutClass>(currentLayout);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mqs = Object.values(LAYOUT_QUERIES).map((q) => window.matchMedia(q));
    const update = () => setLayout(currentLayout());
    mqs.forEach((m) => m.addEventListener?.('change', update));
    window.addEventListener('resize', update);
    update();
    return () => {
      mqs.forEach((m) => m.removeEventListener?.('change', update));
      window.removeEventListener('resize', update);
    };
  }, []);
  return layout;
}

/** Touch-first layouts need 48 px targets (owner addition A; iPad frames). */
export function isTouchLayout(l: LayoutClass): boolean {
  return l === 'phone' || l === 'tablet';
}
